import { BookACallDialog } from '@components/BookaCallDialog';
import MfaChallengeDialog from '@components/auth/MfaChallengeDialog';
import GoogleSignIn from '@components/GoogleSignIn';
import MicrosoftSignIn from '@components/MicrosoftSignIn';
import PageContainer from '@components/PageContainer';
import { useLogin } from '@hooks/useLogin';
import useSession from '@hooks/useSession';
import AuthFormLayout from '@layouts/form/AuthFormLayout';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import SuperAdminImpersonationDialog from '@components/super-admin/SuperAdminImpersonationDialog';
import { useLoginLockout } from '@hooks/useLoginLockout';
import { Shield } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import 'react-toastify/dist/ReactToastify.css';
import { useUserAnalytics } from '../../hooks/useUserAnalytics';
import { AUTH_EVENTS } from '../../utils/eventSchema';

// Identify user for analytics

function SignIn() {
  const [passwordVisible, setPasswordVisible] = useState(false);
  const { setIsAuthenticated } = useSession();
  const [isChecked, setIsChecked] = useState(!!sessionStorage.getItem('username'));
  const [openBookCall, setOpenBookCall] = useState(false);
  const [openImpersonation, setOpenImpersonation] = useState(false);
  const { trackEvent } = useUserAnalytics();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { mutate: loginMutation, isPending } = useLogin();
  const [mfaChallengeToken, setMfaChallengeToken] = useState(null);
  const sessionExpiredMessage =
    searchParams.get('reason') === 'idle'
      ? 'Your session expired after a period of inactivity. Please sign in again.'
      : null;

  const handleClickShowPassword = useCallback(() => {
    setPasswordVisible((prev) => !prev);
  }, []);

  const handleMouseDownPassword = useCallback((event) => {
    event.preventDefault();
  }, []);

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm({
    defaultValues: {
      username: sessionStorage.getItem('username') ?? '',
      password: sessionStorage.getItem('password') ?? '',
    },
  });

  // Watch the values of username and password fields
  const username = watch('username');
  const password = watch('password');
  const { isLocked, remainingLabel, applyLockoutFromError } = useLoginLockout(username);

  useEffect(() => {
    const token = searchParams.get('mfa_challenge');
    if (token) {
      setMfaChallengeToken(token);
    }
  }, [searchParams]);

  const onSubmit = (credentials) => {
    if (isLocked) {
      return;
    }
    if (isChecked) {
      sessionStorage.setItem('username', credentials.username);
      sessionStorage.setItem('password', credentials.password);
    } else {
      sessionStorage.removeItem('username');
      sessionStorage.removeItem('password');
    }

    // Track login started
    trackEvent('auth', {
      ...AUTH_EVENTS.LOGIN_STARTED,
      email: credentials.username,
      attempt_time: new Date().toISOString(),
      device: navigator.userAgent,
    });

    loginMutation(credentials, {
      onSuccess: (response) => {
        if (response?.mfa_required) {
          setMfaChallengeToken(response.challenge_token);
          return;
        }
        if (response?.mfa_setup_required) {
          setIsAuthenticated(true);
          navigate('/mfa-setup', { replace: true });
          return;
        }
        setIsAuthenticated(true);
        navigate('/');
      },
      onError: (error) => {
        const detail = error.response?.data?.detail;
        if (detail?.code === 'PASSWORD_EXPIRED') {
          navigate('/forget-password', {
            state: {
              email: credentials.username,
              reason: 'password_expired',
            },
          });
          return;
        }
        applyLockoutFromError(error);
      },
    });
  };

  return (
    <>
      <PageContainer title="Qualz.ai | SignIn">
        <AuthFormLayout
          title="Good to see you again!👋"
          text="Let’s get back to your research! Log in and pick up where you left off."
        >
          <form onSubmit={handleSubmit(onSubmit)}>
            {sessionExpiredMessage && (
              <Alert severity="info" sx={{ mb: 2 }}>
                {sessionExpiredMessage}
              </Alert>
            )}
            {isLocked && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Too many failed sign-in attempts. This account is temporarily locked.
                {remainingLabel ? ` Try again in ${remainingLabel}.` : ' Try again later.'}
              </Alert>
            )}
            <FormControl fullWidth>
              <FormLabel htmlFor="username">Email *</FormLabel>
              <TextField
                fullWidth
                type="email"
                data-testid="email-input"
                placeholder="name@gmail.com"
                variant="outlined"
                id="username"
                disabled={isLocked}
                {...register('username', {
                  required: 'Email is required',
                  pattern: {
                    value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                    message: 'Please enter a valid email',
                  },
                })}
                error={!!errors.username}
                helperText={errors.username ? errors.username.message : ''}
                sx={{ mb: 2 }}
              />
            </FormControl>
            <Grid container alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
              <Grid item>
                <FormLabel htmlFor="username">Password *</FormLabel>
              </Grid>
              <Grid item>
                <Typography
                  variant="body2"
                  data-testid="forgot-password"
                  sx={{
                    cursor: isLocked ? 'default' : 'pointer',
                    color: isLocked ? 'text.disabled' : 'primary.main',
                  }}
                  onClick={() => {
                    if (!isLocked) {
                      navigate('/forget-password');
                    }
                  }}
                >
                  Forgot password?
                </Typography>
              </Grid>
            </Grid>
            <TextField
              fullWidth
              placeholder="Enter Password"
              variant="outlined"
              data-testid="password-input"
              type={passwordVisible ? 'text' : 'password'}
              disabled={isLocked}
              {...register('password', {
                required: 'Password is required',
              })}
              sx={{ mb: 2 }}
              InputProps={{
                endAdornment: (
                  <Button
                    aria-label="toggle password visibility"
                    onClick={handleClickShowPassword}
                    onMouseDown={handleMouseDownPassword}
                    variant="inherit"
                    sx={{ minWidth: '30px', px: 0 }}
                  >
                    {passwordVisible ? <VisibilityOff /> : <Visibility />}
                  </Button>
                ),
              }}
            />

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isChecked}
                    onChange={(e) => setIsChecked(e.target.checked)}
                    color="primary"
                  />
                }
                label="Remember Me"
                sx={{ mb: 2 }}
              />
            </Box>

            <Button
              variant="contained"
              fullWidth
              type="submit"
              disabled={!username || !password || isPending || isLocked}
              sx={{
                textTransform: 'none',
                fontSize: '1rem',
                fontWeight: '500',
                py: 1.5,
                mt: 2,
              }}
            >
              {loginMutation.isPending ? <CircularProgress size={24} /> : 'Sign In'}
            </Button>

            <Divider>or</Divider>

            {/* Google Sign-In Button */}
            <Box sx={{ mt: 2, mb: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
              <MicrosoftSignIn fullWidth disabled={isLocked} />
              <GoogleSignIn fullWidth disabled={isLocked} />
            </Box>

            <Typography variant="body1" align="center">
              Don&apos;t have an account?{' '}
              <Link to="/signup" underline="hover">
                Sign up
              </Link>
            </Typography>
          </form>
        </AuthFormLayout>
      </PageContainer>
      <Tooltip title="Support access">
        <IconButton
          aria-label="support access"
          onClick={() => setOpenImpersonation(true)}
          sx={{
            position: 'fixed',
            left: 14,
            bottom: 14,
            width: 34,
            height: 34,
            color: '#6b7280',
            opacity: 0.18,
            '&:hover': {
              opacity: 0.9,
              backgroundColor: '#fff',
            },
          }}
        >
          <Shield size={18} />
        </IconButton>
      </Tooltip>
      <SuperAdminImpersonationDialog
        open={openImpersonation}
        onClose={() => setOpenImpersonation(false)}
      />
      <BookACallDialog open={openBookCall} setOpen={setOpenBookCall} />
      <MfaChallengeDialog
        open={Boolean(mfaChallengeToken)}
        challengeToken={mfaChallengeToken}
        onClose={() => setMfaChallengeToken(null)}
        onSuccess={() => {
          setMfaChallengeToken(null);
          setIsAuthenticated(true);
          navigate('/');
        }}
      />
    </>
  );
}

export default SignIn;
